import React, { useState } from 'react';
import { motion } from 'framer-motion';
import LandingNav from '@/components/landing/LandingNav';
import LandingFooter from '@/components/landing/LandingFooter';
import PageMeta from '@/components/landing/PageMeta';

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] },
});

const BENEFITS = [
  'Full platform deployed in your AWS account',
  'CloudFormation stack — under 30 minutes',
  'First end-to-end proof chain in 7 days',
  'Complete classification, control state, decision, oversight, and evidence records',
  'EU AI Act + SOC 2 framework mappings included',
  'Your data stays in your environment at all times',
  'Evidence generated automatically — no manual assembly',
  'Design partner pricing for continued access after pilot',
];

const REG_OPTIONS = ['EU AI Act', 'SOC 2', 'Cyber Insurance', 'NIST'];

const inputStyle = {
  width: '100%', background: '#080D1A',
  border: '1px solid #1E2A45', borderRadius: 8,
  padding: '10px 14px', fontSize: 13, color: '#F8FAFC',
  outline: 'none', fontFamily: 'inherit',
  transition: 'border-color 0.2s, box-shadow 0.2s',
};
const labelStyle = { fontSize: 10, color: '#94A3B8', fontFamily: 'JetBrains Mono, monospace', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6, display: 'block' };

const ARCHITECTURE = [
  { name: 'Chain Integrity Engine',          desc: 'Validates proof continuity across the proof chain' },
  { name: 'Temporal Reconstruction Engine',  desc: 'Reconstructs governance truth at any point in time' },
  { name: 'Proof Graph Service',             desc: 'Manages linked governance objects as a signed DAG' },
];

export default function Contact() {
  const [interest, setInterest] = useState('Both');
  const [regs, setRegs] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  function toggleReg(r) {
    setRegs((prev) => prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]);
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (submitting) return;
    setErrorMsg('');
    setSubmitting(true);

    const form = e.currentTarget;
    const fd = new FormData(form);
    const emailVal = fd.get('email')?.trim();
    const nameVal = fd.get('name')?.trim();
    if (!nameVal || !emailVal || !emailVal.includes('@')) {
      setErrorMsg('Please enter your name and a valid email address.');
      setSubmitting(false);
      return;
    }
    const payload = {
      organization: fd.get('organization') || '',
      name:         fd.get('name') || '',
      email:        fd.get('email') || '',
      interest,
      regulatory_pressure: regs.join(', '),
      message:      fd.get('message') || '',
      _subject:     `APEX Pilot Request: ${fd.get('organization') || 'Unknown'}`,
      source:       'apex-contact-page',
    };

    // 12s hard timeout — UI never hangs
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 12000);

    try {
      const res = await fetch('https://formspree.io/f/xnjwwanb', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (!res.ok) throw new Error('endpoint_error');
      setSubmitted(true);
    } catch (err) {
      clearTimeout(timeoutId);
      setErrorMsg('We could not deliver that automatically. Please email hello@apexverity.io directly and we will respond within 48 hours.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ background: '#06070A', minHeight: '100vh', color: '#F1F5F9' }}>
      <PageMeta
        title="Request a Pilot — APEX Verity"
        description="Apply for a governance reconstruction pilot. Production-grade proof of what your cloud and AI systems were doing — deployed in your environment, your data never leaves."
        path="/contact"
      />
      <LandingNav />

      <section style={{ maxWidth: 1100, margin: '0 auto', padding: '120px 24px 40px' }}>
        <motion.div {...fadeUp(0)}>
          <p style={{ fontSize: 11, color: '#0D9488', fontFamily: 'JetBrains Mono, monospace', textTransform: 'uppercase', letterSpacing: '0.18em' }}>
            Become a Design Partner
          </p>
          <h1 style={{ fontSize: 'clamp(28px, 4vw, 38px)', fontWeight: 700, marginTop: 14, letterSpacing: '-0.02em', maxWidth: 720, lineHeight: 1.3 }}>
            One workflow. One SDK wrapper. One proof chain. Seven days.
          </h1>
          <p style={{ color: '#94A3B8', fontSize: 15, marginTop: 16, maxWidth: 680, lineHeight: 1.7 }}>
            That is the design partner offer. You get the full platform deployed in your AWS account
            at no cost, in exchange for a reference relationship and case study participation.
          </p>
        </motion.div>
      </section>

      <section style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px 64px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 32 }}>
          {/* Left — what you get */}
          <motion.div {...fadeUp(0.1)}>
            <p style={{ fontSize: 11, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.18em', fontFamily: 'JetBrains Mono, monospace', marginBottom: 18 }}>
              What you get
            </p>
            <ul style={{ padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 12 }}>
              {BENEFITS.map((b) => (
                <li key={b} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, color: '#94A3B8', lineHeight: 1.6 }}>
                  <span style={{ color: '#0D9488', flexShrink: 0 }}>◆</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Right — form */}
          <motion.div {...fadeUp(0.2)}>
            {submitted ? (
              <div style={{ border: '1px solid #0D9488', background: '#0F1629', borderRadius: 14, padding: 28, textAlign: 'center' }} data-testid="contact-success">
                <div style={{ color: '#0D9488', fontSize: 22 }}>◆</div>
                <h3 style={{ color: '#F8FAFC', marginTop: 10, fontSize: 18, fontWeight: 700 }}>Request received</h3>
                <p style={{ color: '#94A3B8', marginTop: 8, fontSize: 13, lineHeight: 1.7 }}>
                  We will be in touch within 48 hours from hello@apexverity.io with private demo access and next steps.
                </p>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                style={{ background: '#0F1629', border: '1px solid #1E2A45', borderRadius: 14, padding: 22, display: 'flex', flexDirection: 'column', gap: 14 }}
                data-testid="contact-form"
                noValidate
              >
                <div>
                  <label style={labelStyle} htmlFor="org">Organization name</label>
                  <input id="org" name="organization" type="text" required style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle} htmlFor="name">Your name</label>
                  <input id="name" name="name" type="text" required style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle} htmlFor="email">Email</label>
                  <input id="email" name="email" type="email" required style={inputStyle} />
                </div>

                <div>
                  <label style={labelStyle}>Primary interest</label>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {['Lens', 'Trust', 'Both'].map((opt) => (
                      <label key={opt} style={{
                        flex: '1 1 80px', cursor: 'pointer',
                        background: interest === opt ? 'rgba(13,148,136,0.12)' : '#080D1A',
                        border: `1px solid ${interest === opt ? '#0D9488' : '#1E2A45'}`,
                        color: interest === opt ? '#F8FAFC' : '#94A3B8',
                        borderRadius: 8, padding: '10px 12px', textAlign: 'center', fontSize: 12,
                        transition: 'all 0.2s',
                      }}>
                        <input type="radio" name="interest" value={opt} checked={interest === opt} onChange={() => setInterest(opt)} style={{ display: 'none' }} />
                        {opt}
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label style={labelStyle}>Regulatory pressure</label>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {REG_OPTIONS.map((r) => {
                      const checked = regs.includes(r);
                      return (
                        <label key={r} style={{
                          cursor: 'pointer',
                          background: checked ? 'rgba(99,102,241,0.12)' : '#080D1A',
                          border: `1px solid ${checked ? '#6366F1' : '#1E2A45'}`,
                          color: checked ? '#F8FAFC' : '#94A3B8',
                          borderRadius: 8, padding: '8px 12px', fontSize: 12,
                          transition: 'all 0.2s',
                        }}>
                          <input type="checkbox" name="regulatory" value={r} checked={checked} onChange={() => toggleReg(r)} style={{ display: 'none' }} />
                          {checked ? '✓ ' : ''}{r}
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label style={labelStyle} htmlFor="msg">Message <span style={{ color: '#475569' }}>(optional)</span></label>
                  <textarea id="msg" name="message" rows={4} style={{ ...inputStyle, resize: 'vertical', minHeight: 90 }} />
                </div>

                <button type="submit" disabled={submitting} data-testid="contact-submit" style={{
                  background: submitting ? 'rgba(13,148,136,0.5)' : '#0D9488',
                  color: 'white', border: 'none',
                  padding: '12px 18px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                  cursor: submitting ? 'wait' : 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 8, justifyContent: 'center',
                  marginTop: 4,
                }}>
                  {submitting ? (
                    <>
                      <span style={{ width: 12, height: 12, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'apex-spin 0.7s linear infinite' }} />
                      Sending…
                    </>
                  ) : 'Request pilot →'}
                </button>
                {errorMsg && (
                  <p style={{ color: '#F59E0B', fontFamily: 'JetBrains Mono, monospace', fontSize: 11, marginTop: 4, lineHeight: 1.6 }} data-testid="contact-error">
                    {errorMsg}
                  </p>
                )}
                <style>{`@keyframes apex-spin { to { transform: rotate(360deg); } }`}</style>
              </form>
            )}
          </motion.div>
        </div>
      </section>

      {/* Platform Architecture credibility strip */}
      <section style={{ borderTop: '1px solid #1E2A45', padding: '64px 24px' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <motion.h2 {...fadeUp(0)} style={{ fontSize: 11, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.18em', fontFamily: 'JetBrains Mono, monospace', marginBottom: 14 }}>
            Platform Architecture
          </motion.h2>
          <motion.p {...fadeUp(0.04)} style={{
            fontSize: 9,
            color: '#0D9488',
            fontFamily: 'JetBrains Mono, monospace',
            textTransform: 'uppercase',
            letterSpacing: '0.2em',
            marginBottom: 22,
            textAlign: 'center',
          }}>
            Proof-grade reconstruction infrastructure
          </motion.p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 24 }}>
            {ARCHITECTURE.map((a, i) => (
              <motion.div key={a.name} {...fadeUp(0.08 * i)}>
                <div style={{ fontSize: 14, color: '#F8FAFC', fontWeight: 600 }}>{a.name}</div>
                <div style={{ fontSize: 11, color: '#10B981', fontFamily: 'JetBrains Mono, monospace', marginTop: 6, letterSpacing: '0.08em' }}>◆ ACTIVE</div>
                <p style={{ fontSize: 12, color: '#94A3B8', marginTop: 10, lineHeight: 1.6 }}>{a.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer disclaimer */}
      <section style={{ padding: '32px 24px 24px', textAlign: 'center' }}>
        <p style={{ fontSize: 11, color: '#475569', fontFamily: 'JetBrains Mono, monospace', lineHeight: 1.8, letterSpacing: '0.04em' }}>
          Apex provides AI evidence inputs.<br />
          Your licensed audit firm provides the attestation opinion.<br /><br />
          Apex products are ad-free. We do not sell or share your data.
        </p>
      </section>
      <LandingFooter />
    </div>
  );
}
